// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { EnergySource, IEnergyMeter, ITariffCircuit } from "shared/types/parameter";
import { isSet, roundToDigits } from "shared/utils/utils";
import {
  formatPowerValue,
  formatEnergyValue,
  getGraphBoundaryDates,
  roundTimestamp,
  getDateString,
  checkCategories,
  getSiteSelection,
  getGraphBoundaryDatesFromPeriod,
} from "guiutils";
import { ActionType, IAction, IActionTypeSelectTariffCircuits, raiseAction } from "actions";
import { TimePeriod, DashboardPanel, DataState } from "types";
import { ITariffCircuitData } from "states/datastates";
import { ISiteSelection } from "states/cachedstates";
import { IState } from "states/states";
import { ITrendData, ITrendDataRow } from "states/trendingstates";

import TariffCircuitListModel from "model/tariffcircuitlist";

import { HierarchySelectionState, ITreeViewDropDownOption } from "./TreeViewDropDown";

import cssContent from "constants.module.scss";

const DIGITS_TO_SHOW = 2;

export interface IChartBaseExtProps {
  panel?: DashboardPanel;
  selectedEnergySource?: EnergySource;
  isDashboard?: boolean;
}

export interface IChartBaseProps {
  tariffCircuits: ITariffCircuit[]; // full redraw graph on change
  values?: ITariffCircuitData[] | undefined;
  energyMeters: IEnergyMeter[]; // full redraw graph on change
  selection: ISiteSelection | undefined; // full redraw graph on selectedtariffs, graphmode change
  trendMember: "energy" | "power" | "costs";
  startDate: number | undefined;
  endDate: number | undefined;
  trendData: ITrendData;
  timePeriod: TimePeriod;
  timezoneOffset: number | undefined;
  selectedTimestamp?: number | undefined;
  selectedCategory: string | null | undefined; // full redraw graph on change
  isHierarchyViewMode: boolean; // full redraw graph on change
  dataState: DataState;
  trendLoadingProgress: number;
  filterText?: string;
}

export interface IChartBaseState {}

export interface IChartTariffNode {
  id: number;
  value: number | undefined;
  name: string;
  valueText: string;
  color: string;
  parentId: number | null | undefined;
  maxValue?: number;
  isRelatedToTargetValue?: boolean;
  relatedDateString?: string;
  isDisabled?: boolean;
  hasChildren?: boolean;
  tooltip?: string;
  isVisible?: boolean;
}

export interface ITrendTimestamps {
  startDate: number | undefined;
  endDate: number | undefined;
  usedTimestamps: number[];
}

export const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const selection = getSiteSelection(state);
  const isEnergyPanel = ownProps.panel === DashboardPanel.ENERGY;
  const tariffCircuits = state.para.parameter?.tariffline ?? [];
  const defaultHierarchyViewMode = TariffCircuitListModel.hasHierarchy(tariffCircuits);
  // note: the actual graph boundary zoom is decided per-subclass downstream
  // (see getTimestampsForTrendData), so we only need the raw selected start/end here
  const { selectedStart, selectedEnd, timePeriod } = getGraphBoundaryDatesFromPeriod(
    state.gui.trendPeriod,
    state.site.tzOffset,
  );
  const props = {
    tariffCircuits,
    values: isEnergyPanel ? undefined : state.data.tariffcircuit,
    energyMeters: state.para.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    selection,
    trendMember: isEnergyPanel ? state.gui.trendMember : "power",
    startDate: selectedStart,
    endDate: selectedEnd,
    trendData: state.trending.trendData,
    timePeriod,
    timezoneOffset: state.site.tzOffset,
    selectedTimestamp: state.gui.selectedTimestamp,
    selectedCategory: selection?.category,
    isHierarchyViewMode: selection?.isHierarchyViewMode ?? defaultHierarchyViewMode,
    dataState: state.scheduler.trendLoadingState,
    trendLoadingProgress: state.scheduler.trendLoadingProgress,
    filterText: state.gui.searchFilterText,
  };
  return props;
};

export interface IChartBaseDispatch {
  onHierarchyChanged: (selectedIds: number[] | undefined, isHierarchyViewMode?: boolean) => void;
}

export const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChartBaseDispatch = {
    onHierarchyChanged: (selectedIds: number[] | undefined, isHierarchyViewMode?: boolean) => {
      const payload: IActionTypeSelectTariffCircuits = {
        selectedIds,
        isHierarchyViewMode,
      };
      dispatch(raiseAction(ActionType.ACT_SELECT_TARIFFCIRCUITS, payload));
    },
  };

  return dispatchProps;
};

const messages = defineMessages({
  noCategories: {
    id: "No categories",
  },
});

abstract class ChartBaseComponent<Props, State> extends React.Component<
  Props & IChartBaseExtProps & IChartBaseProps,
  State & IChartBaseState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  // initialize with true, so the first call will also update everything (no snapshot or no shouldComponent update is called on first call)
  protected redrawGraph = true;

  // the internal data-structure looks like:
  // --> allTariffCircuits hold all available tariffs for that view
  protected allTariffCircuits: ITreeViewDropDownOption[] = [];

  private cachedTcModel: TariffCircuitListModel | undefined;
  private cachedStructure: ITreeViewDropDownOption[] | undefined;
  private cachedStructureKey = "";
  private cachedStructureDeps:
    { tariffCircuits: ITariffCircuit[]; energyMeters: IEnergyMeter[] } | undefined;

  shouldComponentUpdate(
    nextProps: Readonly<Props & IChartBaseExtProps & IChartBaseProps>,
    nextState: Readonly<State & IChartBaseState>,
    nextContext: any,
  ): boolean {
    // check if selection has changed...
    // redraw graph
    // always re-render on: HierarchyMode change, graphViewMode change, energySource change,
    // or any change of selected tariffs

    this.redrawGraph = this.shouldRedrawGraph(nextProps, nextState, nextContext);

    return super.shouldComponentUpdate?.(nextProps, nextState, nextContext) ?? true;
  }

  protected shouldRedrawGraph(
    nextProps: Readonly<Props & IChartBaseExtProps & IChartBaseProps>,
    nextState: Readonly<State & IChartBaseState>,
    nextContext: any,
  ): boolean {
    return (
      (this.props.isHierarchyViewMode !== nextProps.isHierarchyViewMode ||
        this.props.selectedEnergySource !== nextProps.selectedEnergySource ||
        this.props.tariffCircuits !== nextProps.tariffCircuits || // should we check for content changes as well?
        this.props.energyMeters !== nextProps.energyMeters || // should we check for content changes as well?
        this.props.selectedCategory !== nextProps.selectedCategory ||
        isSet(this.props.selection?.tariffCircuitIds) !==
          isSet(nextProps.selection?.tariffCircuitIds) ||
        this.props.selection?.tariffCircuitIds?.length !==
          nextProps.selection?.tariffCircuitIds?.length ||
        this.props.selection?.tariffCircuitIds?.some(
          (tc, idx) => nextProps.selection?.tariffCircuitIds?.[idx] !== tc,
        )) ??
      true
    );
  }

  protected prepareHierarchyData() {
    const { tariffCircuits, energyMeters } = this.props;
    const energySource = this.props.selection?.energySource;
    const category = this.props.selection?.category;

    // Rebuild the tree structure only when its inputs change (not on every tariff selection).
    // Use a string key to detect changes to primitive values; use reference equality for arrays.
    const structureKey = `${energySource}|${category}`;
    if (
      !this.cachedStructure ||
      this.cachedTcModel === undefined ||
      this.cachedStructureDeps?.tariffCircuits !== tariffCircuits ||
      this.cachedStructureDeps?.energyMeters !== energyMeters ||
      this.cachedStructureKey !== structureKey
    ) {
      this.cachedTcModel = new TariffCircuitListModel(tariffCircuits, energyMeters);
      this.cachedStructure = this.cachedTcModel.makeOptionsArray(energySource, category);
      this.cachedStructureDeps = { tariffCircuits, energyMeters };
      this.cachedStructureKey = structureKey;
    }

    this.allTariffCircuits = this.cachedTcModel.applySelection(
      this.cachedStructure,
      this.props.selection?.tariffCircuitIds,
      this.props.isHierarchyViewMode,
    );
  }

  protected getSelectedTariffCircuits(
    inShowAll = false,
    inShowAllCategories = false,
    alwaysColor = false,
  ) {
    const availableTariffCircuits = this.allTariffCircuits.filter(
      (entry) =>
        isSet(entry.referenceEntry) &&
        (inShowAll === true ||
          ([HierarchySelectionState.SELECTED, HierarchySelectionState.SELECTED_CHILD].includes(
            entry.checked,
          ) &&
            (inShowAllCategories === true ||
              this.props.selection?.isHierarchyViewMode === true ||
              checkCategories(this.props.selection?.category, entry.referenceEntry?.categories)))),
    );

    const selectedTariffs = this.prepareData(availableTariffCircuits, alwaysColor);

    return { selectedTariffs, tlOptions: availableTariffCircuits };
  }

  // makeChartTariffNode prepares a structure with all necessary information for drawing a tariff-circuit
  //    for EnergyGraph, PieChart, Sankey, TarifCircuitList and TrendDataTable
  //    therefore in the structure are properties which might not get used by some of the graphs/tables
  //
  // parameter: curTariff: the current tariff-circuit option from the hierarchy which should get prepared
  //            trendTimestamps: the timestamps which are relevant for the current trend-data/time-period
  //            additionalTCs: list of additional tariff-circuits which should get shown even if not selected in hierarchy
  //
  // return: prepared IChartTariffNode with all necessary information for drawing
  //
  protected makeChartTariffNode(
    curTariff: ITreeViewDropDownOption,
    trendTimestamps: ITrendTimestamps,
    alwaysColor = false,
    additionalTCs: number[] | undefined = undefined,
    trendDataIndex?: Map<string, ITrendDataRow>,
  ) {
    console.assert(isSet(curTariff?.referenceEntry), "referenceEntry of ITreeViewDropdown missing");
    // cachedTcModel is always initialized in prepareHierarchyData which is called before
    // makeChartTariffNode, and makeChartTariffNode is only called from prepareData which is only
    // called from getSelectedTariffCircuits which is only called from render, so it should never
    // be undefined here (except implmeentation errors), but we need to check it for type safety
    console.assert(isSet(this.cachedTcModel), "cachedTcModel is not initialized");

    const tcIndex = this.props.tariffCircuits.findIndex(
      (curTariffCircuit) => curTariffCircuit === curTariff.referenceEntry,
    );
    const tlId = curTariff.referenceEntry?.id ?? tcIndex;

    // round selectedTimestamp to 30 minutes interval
    const selectedTimestamp =
      isSet(this.props.selectedTimestamp) ?
        Math.round((this.props.selectedTimestamp ?? 0) / 300) * 300
      : undefined;

    const { selectedValue, sumOfValues, maxTargetValue, maxRelateValue } =
      this.cachedTcModel!.getSelectedTimestampValues(
        this.props.trendData,
        tlId,
        this.props.trendMember,
        trendTimestamps.usedTimestamps,
        selectedTimestamp,
        trendDataIndex,
      );

    // check if live-data or selected data should get shown
    const value =
      isSet(this.props.values) ?
        this.props.trendMember === "costs" ? undefined
        : this.props.trendMember === "energy" ? this.props.values?.[tcIndex]?.energy
        : this.props.values?.[tcIndex]?.power
      : selectedValue;

    // prepare text..
    const toLabel =
      this.props.trendMember === "costs" ?
        `${isSet(value) && !isNaN(value) ? roundToDigits(value, DIGITS_TO_SHOW) : "-"} EUR`
      : this.props.trendMember === "energy" ?
        formatEnergyValue(value, curTariff.referenceEntry?.unit)
      : formatPowerValue(value, curTariff.referenceEntry?.unit);

    const showPower = this.props.trendMember !== "costs" && this.props.trendMember !== "energy";

    const relatedDateString =
      getDateString(
        this.props.timePeriod ?? TimePeriod.CUSTOM,
        trendTimestamps.startDate,
        trendTimestamps.endDate,
        !isSet(trendTimestamps.endDate),
        this.context.formatMessage({
          id: `thisperiod_${
            this.props.timePeriod === TimePeriod.CUSTOM ?
              TimePeriod.DAY
            : (this.props.timePeriod ?? "")
          }`,
        }),
        this.context.locale,
      ) ?? "-";

    // prepare values for data/label entry of dataset
    return {
      id: tlId,
      value: isNaN(value ?? 0) ? undefined : value,
      name: curTariff.referenceEntry?.name ?? "---",
      valueText: toLabel,
      color:
        (
          checkCategories(this.props.selectedCategory, curTariff.referenceEntry?.categories) &&
          (alwaysColor === true ||
            curTariff.checked === HierarchySelectionState.SELECTED ||
            curTariff.checked === HierarchySelectionState.SELECTED_CHILD)
        ) ?
          curTariff.color
        : cssContent.TREEVIEW_DISABLED_OPTION,
      parentId: curTariff.referenceEntry?.parent,
      maxValue: showPower ? maxRelateValue : sumOfValues,
      relatedDateString,
      isRelatedToTargetValue: maxTargetValue > 0,
      isDisabled:
        curTariff.checked !== HierarchySelectionState.SELECTED &&
        curTariff.checked !== HierarchySelectionState.SELECTED_CHILD,
      hasChildren: curTariff.hasChildren,
      tooltip:
        (
          isSet(curTariff.referenceEntry?.categories) &&
          curTariff.referenceEntry?.categories.length > 0 &&
          curTariff.referenceEntry?.categories[0] !== ""
        ) ?
          curTariff.referenceEntry?.categories?.join(",")
        : this.context.formatMessage(messages.noCategories),
      isVisible:
        (this.props.isHierarchyViewMode &&
          (curTariff.checked === HierarchySelectionState.SELECTED ||
            curTariff.checked === HierarchySelectionState.SELECTED_CHILD)) ||
        !isSet(additionalTCs) ||
        additionalTCs?.includes(tlId),
    } as IChartTariffNode;
  }

  protected makeChartEnergySourceNode(curTariff: ITreeViewDropDownOption) {
    // prepare values for data/label entry of dataset
    return {
      id: -1,
      value: undefined,
      name: curTariff.text,
      valueText: "",
      color: cssContent.TREEVIEW_DISABLED_OPTION,
      parentId: undefined,
      maxValue: undefined,
      isRelatedToTargetValue: false,
      isDisabled: true,
      hasChildren: false,
      tooltip: "",
    } as IChartTariffNode;
  }

  private prepareData(tlOptionList: ITreeViewDropDownOption[], alwaysColor = false) {
    // get timestamps for selected trend-data depending on the selected time-range
    const trendTimestamps = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod ?? TimePeriod.CUSTOM,
    );

    // Pre-index trendData once so each makeChartTariffNode does O(1) lookups instead of O(n) find
    const trendDataIndex = new Map<string, ITrendDataRow>();
    this.props.trendData?.forEach((row) => {
      trendDataIndex.set(`${row.tlId}:${roundTimestamp(row.timestamp)}`, row);
    });

    return tlOptionList.map((curItem) => {
      return this.makeChartTariffNode(
        curItem,
        trendTimestamps,
        alwaysColor,
        undefined,
        trendDataIndex,
      );
    });
  }

  protected getTimestampsForTrendData(
    trendData: ITrendData,
    inStartDate: number | undefined,
    inEndDate: number | undefined,
    timezoneOffset: number | undefined,
    timePeriod: TimePeriod,
  ) {
    const graphDates = getGraphBoundaryDates(inStartDate, inEndDate, timezoneOffset, timePeriod);
    const roundedTimestamps = trendData
      .filter(
        (r) =>
          (this.props.selection?.tariffCircuitIds?.includes(r.tlId) ?? true) &&
          (!graphDates.startDate || r.timestamp >= graphDates.startDate) &&
          (!graphDates.endDate || r.timestamp < graphDates.endDate),
      )
      .map((item) => roundTimestamp(item.timestamp));
    // sort and remove potential duplicates
    const usedTimestamps = [...new Set(roundedTimestamps.sort((a, b) => a - b))];
    return {
      startDate: graphDates.startDate,
      endDate: graphDates.endDate,
      usedTimestamps,
    };
  }

  protected prepareNextLevel(inModel: TariffCircuitListModel, newParentIdIdx: number): number[] {
    const newParent = this.allTariffCircuits.find(
      (tc) => (tc.referenceEntry?.id ?? tc.entryId.at(-1)) === newParentIdIdx,
    );

    if (isSet(newParent)) {
      this.allTariffCircuits.forEach((entry) => {
        entry.checked = HierarchySelectionState.UNSELECTED;
      });

      this.allTariffCircuits
        .filter((entry) => {
          return (
            entry.entryId.length === newParent.entryId.length + 1 &&
            inModel.isChildOfParent(entry.entryId, newParent.entryId)
          );
        })
        .forEach((entry) => {
          entry.checked = HierarchySelectionState.SELECTED_CHILD;
        });

      newParent.checked = HierarchySelectionState.SELECTED;
    }
    return this.allTariffCircuits
      .filter(
        (tc) =>
          isSet(tc.referenceEntry) &&
          (tc.checked === HierarchySelectionState.SELECTED ||
            tc.checked === HierarchySelectionState.SELECTED_CHILD),
      )
      .map((tc) => tc.referenceEntry?.id ?? tc.entryId.at(-1) ?? -1);
  }
}

export default ChartBaseComponent;
